import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Dialog, Typography, Button as MuiButton } from "@mui/material";
import { useHistory, Link } from "react-router-dom";
import { BellDot, User, Ticket, Dices, Pickaxe, Rocket, ChevronDown, Menu, X } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import { gsap } from "gsap";
import "react-toastify/dist/ReactToastify.css";
import "./navbar.css";

import { Path } from "../../../../static/Constants";
import { networkID } from "../../../../static/utils";
import web3 from "../../../Service/web3";
import {
  getConnectedAccount,
  getSavedAccount,
  connectWallet,
  checkNetwork,
  subscribeToWalletEvents,
} from "../../../../static/walletUtils";
import { colors } from "../../../../theme/colors";
import { typography } from "../../../../theme/typography";

import Notification from "../../notification/Notification";
import { CommonDropdown, CommonDropdownItem } from "../../CommonDropdown/CommonDropdown";
import CommonButton from "../../ui/inputs/CommonButton.js";

// Extracted Animations
import { 
  animateNavHover, 
  animateNavLeave, 
  initNavbarScrollAnimation,
  animateMobileNavBgIn,
  animateMobileNavBgOut,
  initMobileMenuAnimation
} from "../animations";

const NavBar = () => {
  const [user, setUser] = useState(() => getSavedAccount());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileDropdown, setExpandedMobileDropdown] = useState(null);
  const [notifications, setNotifications] = useState([]);
  
  const navbarRef = useRef(null);
  const logoImageRef = useRef(null);
  const logoTextRef = useRef(null);
  const mobileMenuTl = useRef(null);
  const underlineRef = useRef(null);
  
  let history = useHistory();
  const apiUrl = process.env.REACT_APP_API_KEY;

  // =====================================================
  // METAMASK / WEB3 (REACTIVE & PERSISTENT)
  // =====================================================
  const getuser = useCallback(async () => {
    const account = await getConnectedAccount();
    setUser(account);
  }, []);

  useEffect(() => {
    getuser();
    const unsubscribe = subscribeToWalletEvents({
      onAccountsChanged: (acc) => setUser(acc),
      onChainChanged: () => getuser(),
      onDisconnect: () => setUser(""),
    });
    return unsubscribe;
  }, [getuser]);

  const handleConnectWallet = async () => {
    const res = await connectWallet();
    if (res.account) {
      setUser(res.account);

      const netCheck = await checkNetwork(networkID);
      if (!netCheck.isCorrect) {
        setIsModalOpen(true);
        setModalMessage(`Please Connect to Main net (Network Id ${networkID})`);
      }
    } else if (res.error === "NO_WALLET") {
      setIsModalOpen(true);
      setModalMessage(res.message);
    } else if (res.isPending) {
      toast.info(res.message);
    } else if (!res.isCancelled && res.message) {
      toast.error(res.message);
    }
  };

  // =====================================================
  // DATA FETCHING & WEBSOCKETS
  // =====================================================
  const fetchData = async () => {
    try {
      const getNotificationsFromLocalStorage = () => {
        const savedNotifications = localStorage.getItem("notifications");
        if (savedNotifications) {
          setNotifications(JSON.parse(savedNotifications));
        }
      };
      const account = await getConnectedAccount();
      if (!account || !apiUrl) {
        getNotificationsFromLocalStorage();
        return;
      }
      const response = await fetch(`${apiUrl}/notifications?limit=5&skip=0`, {
        method: "GET",
        headers: { Authorization: account },
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        localStorage.setItem("notifications", JSON.stringify(data));
      } else {
        console.error("Error fetching notifications:", response.statusText);
        getNotificationsFromLocalStorage();
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    let ws = null;
    const fetchWSData = async () => {
      try {
        const account = await getConnectedAccount();
        if (!account || !apiUrl) return;

        const wsUrl = apiUrl.replace(/^http/, "ws") + "/ws";
        ws = new WebSocket(wsUrl);
        ws.onopen = () => {
          ws.send(JSON.stringify({ id: account }));
          ws.onmessage = (e) => {
            try {
              let resp = JSON.parse(e.data);
              const storedWSNotifications = localStorage.getItem("wsnotifications") || "[]";
              const wsnotifications = JSON.parse(storedWSNotifications);
              
              if (!wsnotifications.includes(resp.title)) {
                wsnotifications.push(resp.title);
                localStorage.setItem("wsnotifications", JSON.stringify(wsnotifications));
                toast.success(resp.title, {
                  position: "top-right",
                  hideProgressBar: false,
                  onClose: () => {
                    const updatedWSNotifications = wsnotifications.filter((item) => item !== resp.title);
                    localStorage.setItem("wsnotifications", JSON.stringify(updatedWSNotifications));
                  },
                });
              }
            } catch (err) {
              console.warn("Error parsing websocket message:", err);
            }
          };
        };
        ws.onerror = () => {};
      } catch (error) {
        console.warn("WebSocket initialization error:", error);
      }
    };

    fetchWSData();
    return () => {
      if (ws && ws.close) {
        try {
          ws.close();
        } catch {}
      }
    };
  }, [user, apiUrl]);

  // =====================================================
  // UI HANDLERS & GSAP ANIMATIONS
  // =====================================================
  
  // Sticky Navbar Scroll
  useEffect(() => {
    return initNavbarScrollAnimation(navbarRef, logoImageRef, logoTextRef);
  }, []);

  const handleNavHover = (e) => animateNavHover(underlineRef, e.currentTarget);
  const handleNavLeave = () => animateNavLeave(underlineRef);

  // Lock body scroll on mobile menu
  useEffect(() => {
    const handleScrollLock = () => {
      if (isMobileMenuOpen && window.innerWidth <= 1050) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
      } else {
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
        document.body.style.paddingRight = "";
      }
    };

    handleScrollLock();
    window.addEventListener("resize", handleScrollLock);
    return () => {
      window.removeEventListener("resize", handleScrollLock);
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isMobileMenuOpen]);

  // Handle Resize Breakpoints & Timeline Recreation
  useEffect(() => {
    let ctx = initMobileMenuAnimation(navbarRef, mobileMenuTl);

    const handleResize = () => {
      if (window.innerWidth > 1050) {
        setIsMobileMenuOpen(false);
        setExpandedMobileDropdown(null);
      }
      
      const wasMobile = mobileMenuTl.current !== null;
      const isMobile = window.innerWidth <= 1050;
      
      // Only rebuild GSAP context when crossing the breakpoint
      if (wasMobile !== isMobile) {
        if (!isMobile) {
          gsap.set(".navbarcollapse", { clearProps: "opacity,visibility" });
          gsap.set(".mobile-nav-item", { clearProps: "opacity,transform" });
        }
        ctx?.revert();
        ctx = initMobileMenuAnimation(navbarRef, mobileMenuTl);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      mobileMenuTl.current?.kill();
      ctx?.revert();
    };
  }, []);

  // Trigger Mobile Menu Timeline
  useEffect(() => {
    if (window.innerWidth <= 1050 && mobileMenuTl.current) {
      if (isMobileMenuOpen) {
        mobileMenuTl.current.timeScale(1).play();
      } else {
        mobileMenuTl.current.timeScale(1).reverse();
      }
    }
  }, [isMobileMenuOpen]);


  return (
    <Box 
      component="nav"
      ref={navbarRef} 
      className={`navbar autobet-navbar fixed-top ${isMobileMenuOpen ? "expanded" : ""}`}
      style={{
        '--nav-color': colors.secondary.contrastText,
        '--nav-hover-color': colors.primary.light,
        '--nav-font-family': typography.fontFamily,
        '--nav-font-size': typography.body1.fontSize,
        '--nav-font-weight': typography.fontWeightMedium,
      }}
    >
      <Box className="navbar-nav mr-auto">
        <Box component={Link} to={Path.root} className="nav-link Logo ml-sm-3 ml-0">
          <Box
            component="img"
            ref={logoImageRef}
            onClick={(e) => { e.preventDefault(); history.push("/"); }}
            src={require("../../../../static/Images/logo.png")}
            style={{ width: 32, height: 32, cursor: "pointer", marginRight: "6px" }}
          />
          <Box component="span" className="autobet-logo-text">
            <Box component="span" className="autobet-logo-auto">Auto</Box>
            <Box component="span" className="autobet-logo-bet">BET</Box>
          </Box>
        </Box>
      </Box>
      
      <Box className="d-flex align-items-center" style={{ gap: '20px' }}>
        <Box className="mobile-only position-relative">
          <Box className="bell-icon" onClick={() => { history.push("/notifications"); fetchData(); }} style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
            <BellDot size={20} color={colors.secondary.contrastText} />
          </Box>
        </Box>

        {user !== "" ? (
          <Box className="mobile-only" onClick={() => history.push("/profile")} style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
            <User size={24} color={colors.secondary.contrastText} />
            <Box component="span" style={{ fontSize: "9px", fontWeight: 600, color: colors.secondary.contrastText, letterSpacing: "0.03em", lineHeight: 1, opacity: 0.85, whiteSpace: "nowrap" }}>
              {`${user.substring(0, 2)}....${user.substring(user.length - 4, user.length)}`}
            </Box>
          </Box>
        ) : (
          <Box className="mobile-only">
            <CommonButton onClick={() => handleConnectWallet()} variant="contained" size="small" sx={{ borderRadius: "8px", px: 2, py: 0.5 }}>
              Connect
            </CommonButton>
          </Box>
        )}
        
        <Box component="button" aria-controls="basic-navbar-nav" className="navbar-toggler custom-toggler border-0 shadow-none p-0" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={28} color={colors.secondary.contrastText} /> : <Menu size={28} color={colors.secondary.contrastText} />}
        </Box>
      </Box>

      <Box className={`navbar-collapse collapse justify-content-end navbarcollapse ${isMobileMenuOpen ? "show" : ""}`}>
        <Box className="navbar-nav" style={{ position: "relative", alignItems: "center", gap: "1.25rem", flexDirection: 'row' }} onMouseLeave={() => { handleNavLeave(); }}>
          <Box 
            ref={underlineRef} 
            style={{ position: "absolute", bottom: "0px", height: "2px", backgroundColor: colors.primary.light, opacity: 0, pointerEvents: "none", zIndex: 10, left: 0, width: 0, borderRadius: "2px" }} 
          />
          
          {/* Explosive Lotteries Dropdown */}
          <Box 
            component="div"
            className={`nav-link hovernav mobile-nav-item mobile-dropdown-trigger ${expandedMobileDropdown === 'lotteries' ? 'expanded' : ''}`} 
            onMouseEnter={(e) => { 
              if (window.innerWidth > 1050) {
                handleNavLeave();
                setIsDropdownOpen(true);
              } else {
                animateMobileNavBgIn(e);
              }
            }}
            onTouchStart={animateMobileNavBgIn}
            onMouseLeave={(e) => { 
              if (window.innerWidth > 1050) {
                setIsDropdownOpen(false);
              } else {
                animateMobileNavBgOut(e);
              }
            }}
            onTouchEnd={animateMobileNavBgOut}
            onClick={(e) => {
              if (e.target.closest && e.target.closest('.mobile-lottery-submenu')) return;
              e.preventDefault();
              e.stopPropagation();
              if (window.innerWidth <= 1050) setExpandedMobileDropdown(prev => prev === 'lotteries' ? null : 'lotteries');
            }}
            style={{ position: "relative" }}
          >
            <Box className="mobile-hover-bg mobile-only" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '0%', opacity: 0, backgroundColor: colors.primary.light, pointerEvents: 'none', zIndex: 0 }} />
            <Box className="nav-link-content d-flex justify-content-center align-items-center gap-2" style={{ position: 'relative', zIndex: 1, color: colors.neutral.white }}>
              Lottery Hub
              <ChevronDown size={16} strokeWidth={2.5} className={`dropdown-chevron ${expandedMobileDropdown === 'lotteries' ? 'rotated' : ''}`} style={{ marginTop: "2px", transition: 'transform 0.3s ease' }} />
            </Box>

            {/* Desktop dropdown */}
            <Box className="desktop-only">
              <CommonDropdown isVisible={isDropdownOpen} align="left">
                <CommonDropdownItem icon={<Ticket size={20}/>} title="MegaBET" subtitle="Main raffle lottery" to={Path.alllottery} />
                <CommonDropdownItem icon={<Dices size={20}/>} title="SpinBET" subtitle="Spin and win" to={Path.Spinnerlottery} />
                <CommonDropdownItem icon={<Pickaxe size={20}/>} title="MineBET" subtitle="Mine for rewards" to={Path.allminelottery} />
                <CommonDropdownItem icon={<Rocket size={20}/>} title="RocketBET" subtitle="Fast paced lottery" to={Path.allmissilelottery} />
              </CommonDropdown>
            </Box>

            {/* Mobile submenu */}
            <Box className={`mobile-lottery-submenu mobile-only${expandedMobileDropdown === 'lotteries' ? ' mobile-lottery-submenu--open' : ''}`} style={{ position: 'relative', zIndex: 1 }}>
              <Box className="mobile-lottery-inner">
                <Box component={Link} to={Path.alllottery} className="mobile-lottery-item">MegaBET</Box>
                <Box component={Link} to={Path.Spinnerlottery} className="mobile-lottery-item">SpinBET</Box>
                <Box component={Link} to={Path.allminelottery} className="mobile-lottery-item">MineBET</Box>
                <Box component={Link} to={Path.allmissilelottery} className="mobile-lottery-item">RocketBET</Box>
              </Box>
            </Box>
          </Box>

          {/* Standard Navigation Links Iterated */}
          {[
            { name: "Gift Cards", path: Path.allgiftcards },
            { name: "Results", path: Path.result },
            { name: "Lottery Creator", path: Path.org },
            { name: "News", path: Path.news },
            { name: "Help", path: Path.help },
            { name: "About Us", path: Path.about },
          ].map((link) => (
            <Box
              component={Link}
              key={link.name}
              onMouseEnter={(e) => { handleNavHover(e); animateMobileNavBgIn(e); }}
              onTouchStart={animateMobileNavBgIn}
              onMouseLeave={animateMobileNavBgOut}
              onTouchEnd={animateMobileNavBgOut}
              className={`nav-link hovernav mobile-nav-item ${window.location.pathname.includes(link.path) ? "activenav" : ""}`}
              to={link.path}
              style={{ position: 'relative', textDecoration: 'none' }}
            >
              <Box className="mobile-hover-bg mobile-only" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '0%', opacity: 0, backgroundColor: colors.primary.light, pointerEvents: 'none', zIndex: 0 }} />
              <Box className="nav-link-content" style={{ position: 'relative', zIndex: 1, color: colors.neutral.white }}>{link.name}</Box>
            </Box>
          ))}

          <Box className="desktop-only">
            <Box component="div" className="nav-link hovernav d-flex" onMouseEnter={() => { handleNavLeave(); setIsNotifOpen(true); fetchData(); }} onMouseLeave={() => setIsNotifOpen(false)} style={{ position: "relative" }}>
              <Box className="bell-icon" onClick={() => { history.push("/notifications"); fetchData(); }} style={{ display: "flex", alignItems: "center" }}>
                <BellDot size={18} style={{ marginBottom: "2px" }} />
              </Box>
              <CommonDropdown isVisible={isNotifOpen} align="right">
                <Notification notifications={notifications} />
              </CommonDropdown>
            </Box>
          </Box>

          <Box className="desktop-only">
            {user === "" && (
              <Box component="div" className="nav-link" onMouseEnter={handleNavLeave} style={{ padding: "0 10px" }}>
                <CommonButton onClick={() => handleConnectWallet()} variant="contained" size="medium" sx={{ borderRadius: "8px", px: 3, py: 1 }}>
                  Connect
                </CommonButton>
              </Box>
            )}
          </Box>
          
          <Box className="desktop-only">
            {user !== "" && (
              <Box component={Link} to={Path.profile} className="nav-link accNAdd d-flex align-items-center hovernav" onMouseEnter={handleNavLeave} style={{ gap: "0.5rem", textDecoration: 'none' }}>
                <Box component="span" className="AccountAddress" style={{ color: "inherit", fontSize: "inherit", fontWeight: "inherit" }}>
                  {`${user.substring(0, 2)}.... ${user.substring(user.length - 4, user.length)}`}
                </Box>
                <User size={22} color="currentColor" style={{ cursor: "pointer" }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Dialog classes={{ paper: "PopupStyle modal-content" }} open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box className="modal-body">
          <Box className="container">
            <Box className="row">
              <Box className="col-lg-12 col-12">
                <Box className="message-text">{modalMessage}</Box>
                <Typography component="p" className="link-textt" style={{ marginTop: "16px" }}>
                  <Box component={Link} to="/help" style={{ color: colors.primary.main, textDecoration: "none", fontWeight: 500 }}>
                    Get Help
                  </Box>
                </Typography>
              </Box>
              <Box className="col-12">
                <Box className="btn-div">
                  <MuiButton variant="contained" className="submit-btn btn btn-primary" onClick={() => setIsModalOpen(false)}>
                    OK
                  </MuiButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>
      <ToastContainer autoClose={5000} />
    </Box>
  );
};

export default NavBar;